import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { WORKFLOW_INBOX_PERMISSIONS } from '../../config/workflowCapabilities';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { createDefaultWorkflowFilters } from '../../utils/workflowFilters';

type Props = NativeStackScreenProps<RootStackParamList, 'WorkflowFilter'>;

/**
 * Pending inbox filters supported by GET /workflows/approvals/pending are
 * entityType, page, and pageSize only. Date and status filters are not mounted.
 */
export function WorkflowFilterScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const current = route.params.currentFilters;
  const [entityType, setEntityType] = useState(current.entityType ?? '');
  const [error, setError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      help: {
        ...typography.caption,
        color: palette.textMuted,
        marginBottom: spacing.lg,
      },
      error: {
        ...typography.body,
        color: palette.error,
        marginTop: spacing.sm,
      },
    }),
    [palette],
  );

  const apply = () => {
    const trimmed = entityType.trim();
    if (trimmed && !/^[A-Za-z][A-Za-z0-9_]*$/.test(trimmed)) {
      setError('Request type contains invalid characters.');
      return;
    }
    navigation.navigate({
      name: routes.workflowInbox,
      params: {
        appliedFilters: trimmed
          ? { entityType: trimmed.toUpperCase() }
          : {},
      },
      merge: true,
    });
  };

  const reset = () => {
    const defaults = createDefaultWorkflowFilters();
    setEntityType(defaults.entityType ?? '');
    setError(null);
  };

  return (
    <PermissionGuard
      anyPermissions={WORKFLOW_INBOX_PERMISSIONS}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader
          title="Approval Filters"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <Text style={themed.help}>
          Optional request type uses the backend entityType code, for example
          LEAVE_APPLICATION. Leave blank to show all pending approvals. Date and
          status filters are not available on the current API.
        </Text>
        <AppInput
          label="Request type (optional)"
          value={entityType}
          onChangeText={value => {
            setEntityType(value);
            setError(null);
          }}
          placeholder="LEAVE_APPLICATION"
          autoCapitalize="characters"
        />
        {error ? (
          <Text accessibilityRole="alert" style={themed.error}>
            {error}
          </Text>
        ) : null}
        <View style={styles.actions}>
          <AppButton title="Apply" onPress={apply} />
          <AppButton title="Reset" variant="secondary" onPress={reset} />
          <AppButton
            title="Cancel"
            variant="text"
            onPress={() => navigation.goBack()}
          />
        </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  actions: { gap: spacing.sm, marginTop: spacing.xl },
});
